import { Box, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import AdditionalInfo from "@/components/common/additionalInfo/additionalInfo";
import SendNewCodeButton from "@/components/common/buttons/sendNewCodeButton";
import OTPInput from "@/components/common/otpInput/otpInput";
import { useClientBookingContext } from "@/contexts/clientBookingContext";
import { useGenerateBooking2FaForClientMutation } from "@/graphql/mutations/generateBooking2FAForClient.graphql.types";
import { useVerifyClient2FaCodeMutation } from "@/graphql/mutations/verifyClient2FACode.graphql.types";
import { BookingStep } from "@/types/bookingTypes";
import { BookingReturningClientErrors } from "@/utils/booking";
import { hasDjangoMutationError } from "@/utils/djangoMutationError";
import useErrorLogger from "@/utils/useErrorLogger";
import BookingInfoCard from "../bookingInfoCard";

export default function BookingCodeInput() {
  const logError = useErrorLogger();
  const {
    clientDetails,
    setStep,
    medspaData,
    setClientDetails,
    setCard,
    setCode,
    codeExpired,
    setCodeExpired,
    setIsReturningClient,
  } = useClientBookingContext();

  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(30);
  const [inputDisabled, setInputDisabled] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout>(null);
  const [errorMessage, setErrorMessage] = useState("");

  const [verifyClient2FACode] = useVerifyClient2FaCodeMutation();
  const [generateBooking2FAForClient] =
    useGenerateBooking2FaForClientMutation();

  const verifyCode = async (code: string) => {
    setInputDisabled(true);
    try {
      const { data } = await verifyClient2FACode({
        variables: {
          clientPhoneNumber: clientDetails.phone,
          code,
          medspaSlug: medspaData.slug,
        },
      });

      const { firstName, lastName, email, card } =
        data.verifyClient2faCode.clientData;

      setClientDetails((prev) => ({ ...prev, firstName, lastName, email }));
      setCode(code);
      setCard(card);
      setCodeExpired(false);
      setIsReturningClient(true);
      setStep(BookingStep.RETURNING_CLIENT);
    } catch (e) {
      if (e.message === BookingReturningClientErrors.INCORRECT_CODE) {
        setErrorMessage(
          "The code you've entered is incorrect, please double check the SMS we sent and try again. If you didn't receive a code, click below to request a new one."
        );
      }
      if (e.message === BookingReturningClientErrors.CODE_EXPIRED) {
        setErrorMessage(
          "The code you've entered has expired. Please use the button below to send new code."
        );
      }
      setInputDisabled(false);
      setOtp("");

      if (
        ![
          BookingReturningClientErrors.CODE_EXPIRED,
          BookingReturningClientErrors.INCORRECT_CODE,
        ].includes(e.message)
      ) {
        logError(e.message);
        toast.error(
          hasDjangoMutationError(e)
            ? e.message
            : "Unable to verify code. Please contact support team."
        );
      }
    }
  };

  const onOtpChange = (value: string) => {
    setOtp(value);
    setErrorMessage("");
    if (value.trim().length === 6) {
      verifyCode(value);
    }
  };

  const sendNewCode = () => {
    setTimer(30);
    intervalRef.current = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);
    generateBooking2FAForClient({
      variables: {
        clientPhoneNumber: clientDetails.phone,
        medspaSlug: medspaData.slug,
      },
    });
  };

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (timer <= 0) clearInterval(intervalRef.current);
  }, [timer]);

  return (
    <BookingInfoCard>
      <Typography
        variant="h3"
        sx={{
          color: "text.primary",
          pb: 3,
        }}
      >
        Welcome back!
      </Typography>
      <Stack spacing={2}>
        {codeExpired && (
          <AdditionalInfo
            variant="warning"
            text="Oops, your session has expired. Please verify your phone number with another code that we've sent to you."
          />
        )}
        <Typography>
          We&apos;ve sent you a 6-digit security code to your mobile number
          ending in <strong>{clientDetails?.phone?.slice(-4)}</strong>. It will
          expire in 10 minutes. Please enter the code in the field below to
          finalize your appointment.
        </Typography>
        <OTPInput
          onChange={onOtpChange}
          valueLength={6}
          value={otp}
          inputDisabled={inputDisabled}
        />

        {errorMessage && (
          <Box>
            <Typography
              sx={{
                color: "error.main",
              }}
            >
              {errorMessage}
            </Typography>
          </Box>
        )}
        <Box>
          <SendNewCodeButton timer={timer} onClick={sendNewCode} />
        </Box>
      </Stack>
    </BookingInfoCard>
  );
}
